const i18n = require("locales/i18n");

/**
 * Sets the `__esModule` property on the `exports` object to `true`,
 * indicating that the module is an ES module.
 */
Object.defineProperty(exports, "__esModule", { value: true });

/**
 * Augments a module namespace object to be compatible with ES module syntax.
 * This function ensures that the module has an `__esModule` property and that all
 * exports are properly defined on the returned object.
 * @param {Object} n - The module namespace object to augment.
 * @returns {Object} The augmented module namespace object with an `__esModule` property.
 */
function getAugmentedNamespace(n) {
  if (n.__esModule) return n;
  var a = Object.defineProperty({}, "__esModule", { value: true });
  Object.keys(n).forEach(function (k) {
    var d = Object.getOwnPropertyDescriptor(n, k);
    Object.defineProperty(
      a,
      k,
      d.get
        ? d
        : {
            enumerable: true,
            get: function () {
              return n[k];
            },
          }
    );
  });
  return a;
}

/**
 * A rule that checks that sequence flows outgoing from a
 * conditional forking gateway or activity are
 * either default flows _or_ have a condition attached
 * Validates conditional flows in a BPMN diagram to ensure that each conditional
 * flow has a condition expression or is marked as the default flow.
 * @param {Object} node - The BPMN node to check. It should be a conditional forking node.
 * @param {Object} reporter - The reporter object used to report any issues found during validation.
 * @param {Function} isConditionalForking - A function that checks if the node is a conditional forking node.
 * @param {Function} hasCondition$2 - A function that checks if a flow has a condition expression.
 * @param {Function} isDefaultFlow$1 - A function that checks if a flow is the default flow.
 * @param {Object} i18n - An internationalization object used to translate error messages.
 * @returns {void}
 */
var conditionalFlows = function () {
  function check(node, reporter) {
    if (!isConditionalForking(node)) {
      return;
    }
    const outgoing = node.outgoing || [];
    outgoing.forEach((flow) => {
      const missingCondition = !hasCondition$2(flow) && !isDefaultFlow$1(node, flow);

      if (missingCondition) {
        reporter.report(flow.id, i18n.default.t("bpmnLint.AlertSequenceflowMissingCondition"), [
          "conditionExpression",
        ]);
      }
    });
  }

  return {
    check,
  };
};

/**
 * Checks if a BPMN node is a conditional forking node.
 * A node is considered a conditional forking node if it has a default flow
 * or if any of its outgoing flows have a condition expression.
 * @param {Object} node - The BPMN node to check.
 * @param {Function} hasCondition$2 - A function that checks if a flow has a condition expression.
 * @returns {boolean} Returns `true` if the node is a conditional forking node, otherwise `false`.
 */
function isConditionalForking(node) {
  const defaultFlow = node["default"];
  const outgoing = node.outgoing || [];

  return defaultFlow || outgoing.find(hasCondition$2);
}

/**
 * Checks if a BPMN flow has a condition expression.
 * @param {Object} flow - The BPMN flow to check.
 * @returns {boolean} Returns `true` if the flow has a condition expression, otherwise `false`.
 */
function hasCondition$2(flow) {
  return !!flow.conditionExpression;
}

/**
 * Checks if a BPMN flow is the default flow of a node.
 * @param {Object} node - The BPMN node to check against.
 * @param {Object} flow - The BPMN flow to check.
 * @returns {boolean} Returns `true` if the flow is the default flow of the node, otherwise `false`.
 */
function isDefaultFlow$1(node, flow) {
  return node["default"] === flow;
}

/**
 * Checks whether node is of specific bpmn type.
 * @param {ModdleElement} node
 * @param {String} type
 * @return {Boolean}
 */
function is$d(node, type) {
  if (type.indexOf(":") === -1) {
    type = "bpmn:" + type;
  }

  if (typeof node.$instanceOf === "function") {
    return node.$instanceOf(type);
  } else {
    return node.$type === type;
  }
}

/**
 * Checks whether node has any of the specified types.
 * @param {ModdleElement} node
 * @param {Array<String>} types
 * @return {Boolean}
 */
function isAny$6(node, types) {
  return types.some(function (type) {
    return is$d(node, type);
  });
}

/**
 * Creates a frozen object `index_esm$1` containing the functions `is` and `isAny`.
 * Uses `getAugmentedNamespace` to augment the namespace of `index_esm$1` and assigns it to `require$$0`.
 * Destructures `is` and `isAny` from `require$$0` with aliases `is$c` and `isAny$5`.
 */
var index_esm$1 = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  is: is$d,
  isAny: isAny$6,
});
var require$$0 = /*@__PURE__*/ getAugmentedNamespace(index_esm$1);
const { is: is$c, isAny: isAny$5 } = require$$0;

/**
 * A rule that checks the presence of an end event per scope.
 * Checks if a BPMN node contains at least one UserTask or ManualTask.
 * @param {Object} node - The BPMN node to check.
 * @param {Function} is$c - A function to check if a node is of a specific type.
 * @returns {boolean} Returns `true` if the node contains at least one UserTask or ManualTask, otherwise `false`.
 */
var TaskRequired = function () {
  function hasManualTask(node) {
    const flowElements = node.flowElements || [];

    return flowElements.some(
      (node) => is$c(node, "bpmn:UserTask") || is$c(node, "bpmn:ManualTask")
    );
  }

  function check(node, reporter) {
    if (!isAny$5(node, ["bpmn:Process", "bpmn:SubProcess"])) {
      return;
    }

    if (!hasManualTask(node)) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertTaskRequired"));
    }
  }

  return { check };
};

/**
 * Checks if the given node contains at least one EndEvent.
 * @param {Object} node - The BPMN node to check (should be a Process or SubProcess).
 * @returns {boolean} Returns `true` if the node contains at least one EndEvent, otherwise `false`.
 */
var endEventRequired = function () {
  function hasEndEvent(node) {
    const flowElements = node.flowElements || [];

    return flowElements.some((node) => is$c(node, "bpmn:EndEvent"));
  }

  function check(node, reporter) {
    if (!isAny$5(node, ["bpmn:Process", "bpmn:SubProcess"])) {
      return;
    }

    if (!hasEndEvent(node)) {
      let type = null;
      if (is$c(node, "bpmn:SubProcess")) {
        type = i18n.default.t("bpmnLint.AlertEndEventRequired");
      } else {
        type = i18n.default.t("bpmnLint.AlertEndEventRequired");
      }

      reporter.report(node.id, type);
    }
  }

  return { check };
};

const { is: is$b } = require$$0;

/**
 * Checks if a BPMN SubProcess node is triggered by an event and ensures
 * that it contains at least one StartEvent with event definitions.
 * Reports an issue if a StartEvent lacks event definitions.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log issues.
 * @returns {Object} An object containing the check function.
 */
var eventSubProcessTypedStartEvent = function () {
  function check(node, reporter) {
    if (!is$b(node, "bpmn:SubProcess") || !node.triggeredByEvent) {
      return;
    }

    const flowElements = node.flowElements || [];

    flowElements.forEach(function (flowElement) {
      if (!is$b(flowElement, "bpmn:StartEvent")) {
        return false;
      }

      const eventDefinitions = flowElement.eventDefinitions || [];

      if (eventDefinitions.length === 0) {
        reporter.report(
          flowElement.id,
          i18n.default.t("bpmnLint.AlertEventSubProcessTypedStartEvent"),
          ["eventDefinitions"]
        );
      }
    });
  }

  return {
    check,
  };
};

/**
 * A rule that checks that no fake join is modeled by attempting
 * to give a task or event join semantics.
 * Users should model a parallel joining gateway
 * to achieve the desired behavior.
 */
const { is: is$a, isAny: isAny$3 } = require$$0;

/**
 * Checks if a BPMN node requires a label and reports it if necessary.
 * This function ignores certain BPMN elements such as ParallelGateways,
 * EventBasedGateways, joining Gateways, BoundaryEvents, SubProcesses,
 * and SequenceFlows without conditions. It logs unnamed UserTasks,
 * ManualTasks, and SendTasks for debugging purposes.
 * If a node is a FlowNode, SequenceFlow, Participant, or Lane and lacks
 * a name, it reports the issue using the provided reporter.
 * @param {Object} node - The BPMN node to check.
 * @param {Object} reporter - The reporter used to log issues.
 */
var labelRequired = function () {
  function check(node, reporter) {
    if (isAny$3(node, ["bpmn:ParallelGateway", "bpmn:EventBasedGateway"])) {
      return;
    }

    // ignore joining gateways
    if (is$a(node, "bpmn:Gateway") && !isForking(node)) {
      return;
    }

    if (is$a(node, "bpmn:BoundaryEvent")) {
      return;
    }

    // ignore sub-processes
    if (is$a(node, "bpmn:SubProcess")) {
      return;
    }

    // ignore sequence flow without condition
    if (is$a(node, "bpmn:SequenceFlow") && !hasCondition$1(node)) {
      return;
    }

    // ignore data objects and artifacts for now
    if (isAny$3(node, ["bpmn:FlowNode", "bpmn:SequenceFlow", "bpmn:Participant", "bpmn:Lane"])) {
      const name = (node.name || "").trim();

      if (name.length === 0) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertLabelRequired"));
      }
    }
  }

  return { check };
};

/**
 * Determines if a given node is forking.
 * A node is considered forking if it has more than one outgoing connection.
 * @param {Object} node - The node to check.
 * @returns {boolean} True if the node is forking, otherwise false.
 */
function isForking(node) {
  const outgoing = node.outgoing || [];

  return outgoing.length > 1;
}

function hasCondition$1(node) {
  return node.conditionExpression;
}

/**
 * Flatten array, one level deep.
 * @param {Array<?>} arr
 * @return {Array<?>}
 */
function flatten$1(arr) {
  return Array.prototype.concat.apply([], arr);
}

var nativeToString = Object.prototype.toString;
var nativeHasOwnProperty = Object.prototype.hasOwnProperty;
function isUndefined(obj) {
  return obj === undefined;
}
function isDefined(obj) {
  return obj !== undefined;
}
function isNil(obj) {
  return obj == null;
}
function isArray(obj) {
  return nativeToString.call(obj) === "[object Array]";
}
function isObject(obj) {
  return nativeToString.call(obj) === "[object Object]";
}
function isNumber(obj) {
  return nativeToString.call(obj) === "[object Number]";
}
function isFunction(obj) {
  var tag = nativeToString.call(obj);
  return (
    tag === "[object Function]" ||
    tag === "[object AsyncFunction]" ||
    tag === "[object GeneratorFunction]" ||
    tag === "[object AsyncGeneratorFunction]" ||
    tag === "[object Proxy]"
  );
}
function isString(obj) {
  return nativeToString.call(obj) === "[object String]";
}

/**
 * Ensure collection is an array.
 * @param {Object} obj
 */
function ensureArray(obj) {
  if (isArray(obj)) {
    return;
  }

  throw new Error(i18n.default.t("bpmnLint.AlertEnsureArray"));
}

/**
 * Return true, if target owns a property with the given key.
 * @param {Object} target
 * @param {String} key
 * @return {Boolean}
 */
function has(target, key) {
  return nativeHasOwnProperty.call(target, key);
}

/**
 * Find element in collection.
 * @param  {Array|Object} collection
 * @param  {Function|Object} matcher
 * @return {Object}
 */
function find(collection, matcher) {
  matcher = toMatcher(matcher);
  var match;
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      match = val;
      return false;
    }
  });
  return match;
}

/**
 * Find element index in collection.
 * @param  {Array|Object} collection
 * @param  {Function} matcher
 * @return {Object}
 */
function findIndex(collection, matcher) {
  matcher = toMatcher(matcher);
  var idx = isArray(collection) ? -1 : undefined;
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      idx = key;
      return false;
    }
  });
  return idx;
}

/**
 * Find element in collection.
 * @param  {Array|Object} collection
 * @param  {Function} matcher
 * @return {Array} result
 */
function filter(collection, matcher) {
  var result = [];
  forEach(collection, function (val, key) {
    if (matcher(val, key)) {
      result.push(val);
    }
  });
  return result;
}

/**
 * Iterate over collection; returning something
 * (non-undefined) will stop iteration.
 * @param  {Array|Object} collection
 * @param  {Function} iterator
 * @return {Object} return result that stopped the iteration
 */
function forEach(collection, iterator) {
  var val, result;

  if (isUndefined(collection)) {
    return;
  }

  var convertKey = isArray(collection) ? toNum : identity;

  for (var key in collection) {
    if (has(collection, key)) {
      val = collection[key];
      result = iterator(val, convertKey(key));

      if (result === false) {
        return val;
      }
    }
  }
}

/**
 * Return collection without element.
 * @param  {Array} arr
 * @param  {Function} matcher
 * @return {Array}
 */
function without(arr, matcher) {
  if (isUndefined(arr)) {
    return [];
  }

  ensureArray(arr);
  matcher = toMatcher(matcher);
  return arr.filter(function (el, idx) {
    return !matcher(el, idx);
  });
}

/**
 * Reduce collection, returning a single result.
 * @param  {Object|Array} collection
 * @param  {Function} iterator
 * @param  {Any} result
 * @return {Any} result returned from last iterator
 */
function reduce(collection, iterator, result) {
  forEach(collection, function (value, idx) {
    result = iterator(result, value, idx);
  });
  return result;
}

/**
 * Return true if every element in the collection
 * matches the criteria.
 * @param  {Object|Array} collection
 * @param  {Function} matcher
 * @return {Boolean}
 */
function every(collection, matcher) {
  return !!reduce(
    collection,
    function (matches, val, key) {
      return matches && matcher(val, key);
    },
    true
  );
}

/**
 * Return true if some elements in the collection
 * match the criteria.
 * @param  {Object|Array} collection
 * @param  {Function} matcher
 * @return {Boolean}
 */
function some(collection, matcher) {
  return !!find(collection, matcher);
}

/**
 * Transform a collection into another collection
 * by piping each member through the given fn.
 * @param  {Object|Array}   collection
 * @param  {Function} fn
 * @return {Array} transformed collection
 */
function map(collection, fn) {
  var result = [];
  forEach(collection, function (val, key) {
    result.push(fn(val, key));
  });
  return result;
}

/**
 * Get the collections keys.
 * @param  {Object|Array} collection
 * @return {Array}
 */
function keys(collection) {
  // eslint-disable-next-line
  return (collection && Object.keys(collection)) || [];
}

/**
 * Shorthand for `keys(o).length`.
 * @param  {Object|Array} collection
 * @return {Number}
 */
function size(collection) {
  return keys(collection).length;
}

/**
 * Get the values in the collection.
 * @param  {Object|Array} collection
 * @return {Array}
 */
function values(collection) {
  return map(collection, function (val) {
    return val;
  });
}

/**
 * Group collection members by attribute.
 * @param  {Object|Array} collection
 * @param  {Function} extractor
 * @return {Object} map with { attrValue => [ a, b, c ] }
 */
function groupBy(collection, extractor) {
  var grouped = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  extractor = toExtractor(extractor);
  forEach(collection, function (val) {
    var discriminator = extractor(val) || "_";
    var group = grouped[discriminator];

    if (!group) {
      group = grouped[discriminator] = [];
    }

    group.push(val);
  });
  return grouped;
}
function uniqueBy(extractor) {
  extractor = toExtractor(extractor);
  var grouped = {};

  for (
    var _len = arguments.length, collections = new Array(_len > 1 ? _len - 1 : 0), _key = 1;
    _key < _len;
    _key++
  ) {
    collections[_key - 1] = arguments[_key];
  }

  forEach(collections, function (c) {
    return groupBy(c, extractor, grouped);
  });
  var result = map(grouped, function (val) {
    return val[0];
  });
  return result;
}
var unionBy = uniqueBy;

/**
 * Sort collection by criteria.
 * @param  {Object|Array} collection
 * @param  {String|Function} extractor
 * @return {Array}
 */
function sortBy(collection, extractor) {
  extractor = toExtractor(extractor);
  var sorted = [];
  forEach(collection, function (value, key) {
    var disc = extractor(value, key);
    var entry = {
      d: disc,
      v: value,
    };

    for (var idx = 0; idx < sorted.length; idx++) {
      var d = sorted[idx].d;

      if (disc < d) {
        sorted.splice(idx, 0, entry);
        return;
      }
    } // not inserted, append (!)

    sorted.push(entry);
  });
  return map(sorted, function (e) {
    return e.v;
  });
}

/**
 * Create an object pattern matcher.
 * const matcher = matchPattern({ id: 1 });
 * let element = find(elements, matcher);
 * @param  {Object} pattern
 * @return {Function} matcherFn
 */
function matchPattern(pattern) {
  return function (el) {
    return every(pattern, function (val, key) {
      return el[key] === val;
    });
  };
}

/**
 * Converts an extractor into a function if it is not already one.
 * @param {Function|string} extractor - The extractor to be converted.
 * If a function, it is returned as is. If a string, a function is returned
 * that extracts the property with the name of the string from an object.
 * @returns {Function} A function that extracts a value from an object.
 */
function toExtractor(extractor) {
  return isFunction(extractor)
    ? extractor
    : function (e) {
        return e[extractor];
      };
}

/**
 * Converts a given matcher into a function. If the matcher is already a function,
 * it returns the matcher itself. Otherwise, it returns a function that checks
 * if a given element is strictly equal to the matcher.
 * @param {Function|*} matcher - The matcher to convert into a function.
 * @returns {Function} A function that can be used to match elements.
 */
function toMatcher(matcher) {
  return isFunction(matcher)
    ? matcher
    : function (e) {
        return e === matcher;
      };
}

/**
 * Returns the argument passed to it without any modification.
 * @param {*} arg - The input value to be returned.
 * @returns {*} The same value that was passed as the argument.
 */
function identity(arg) {
  return arg;
}

/**
 * Converts the given argument to a number.
 * @param {any} arg - The value to be converted to a number.
 * @returns {number} The numeric representation of the argument.
 */
function toNum(arg) {
  return Number(arg);
}

/**
 * Debounce fn, calling it only once if the given time
 * elapsed between calls.
 * Lodash-style the function exposes methods to `#clear`
 * and `#flush` to control internal behavior.
 * @param  {Function} fn
 * @param  {Number} timeout
 * @return {Function} debounced function
 */
function debounce(fn, timeout) {
  var timer;
  var lastArgs;
  var lastThis;
  var lastNow;

  function fire(force) {
    var now = Date.now();
    var scheduledDiff = force ? 0 : lastNow + timeout - now;

    if (scheduledDiff > 0) {
      return schedule(scheduledDiff);
    }

    fn.apply(lastThis, lastArgs);
    clear();
  }

  function schedule(timeout) {
    timer = setTimeout(fire, timeout);
  }

  function clear() {
    if (timer) {
      clearTimeout(timer);
    }

    timer = lastNow = lastArgs = lastThis = undefined;
  }

  function flush() {
    if (timer) {
      fire(true);
    }

    clear();
  }

  function callback() {
    lastNow = Date.now();

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    lastArgs = args;
    lastThis = this; // ensure an execution is scheduled

    if (!timer) {
      schedule(timeout);
    }
  }

  callback.flush = flush;
  callback.cancel = clear;
  return callback;
}

/**
 * Throttle fn, calling at most once
 * in the given interval.
 * @param  {Function} fn
 * @param  {Number} interval
 * @return {Function} throttled function
 */
function throttle(fn, interval) {
  var throttling = false;
  return function () {
    if (throttling) {
      return;
    }

    fn.apply(void 0, arguments);
    throttling = true;
    setTimeout(function () {
      throttling = false;
    }, interval);
  };
}

/**
 * Bind function against target <this>.
 * @param  {Function} fn
 * @param  {Object}   target
 * @return {Function} bound function
 */
function bind(fn, target) {
  return fn.bind(target);
}

/**
 * Determines the type of the given object, with special handling for Symbol objects.
 * This function checks if the environment supports the Symbol type and its iterator.
 * If so, it returns a function that simply uses the `typeof` operator.
 * Otherwise, it returns a function that checks if the object is a Symbol and returns
 * "symbol" if true, or uses the `typeof` operator for other types.
 * @param {any} obj - The object whose type is to be determined.
 * @returns {string} - The type of the object as a string.
 */
function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    // eslint-disable-next-line
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    // eslint-disable-next-line
    _typeof = function (obj) {
      return obj &&
        typeof Symbol === "function" &&
        obj.constructor === Symbol &&
        obj !== Symbol.prototype
        ? "symbol"
        : typeof obj;
    };
  }
  // eslint-disable-next-line
  return _typeof(obj);
}

/**
 * Extends an object with properties from one or more source objects.
 * This function uses `Object.assign` if available, otherwise it manually
 * copies properties from source objects to the target object. It iterates
 * over each source object and copies its own enumerable properties to the
 * target object.
 * @param {Object} target - The target object to extend.
 * @param {...Object} sources - One or more source objects to copy properties from.
 * @returns {Object} The extended target object.
 */
function _extends() {
  // eslint-disable-next-line
  _extends =
    Object.assign ||
    function (target) {
      for (var i = 1; i < arguments.length; i++) {
        var source = arguments[i];

        for (var key in source) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            target[key] = source[key];
          }
        }
      }

      return target;
    };

  return _extends.apply(this, arguments);
}

/**
 * Convenience wrapper for `Object.assign`.
 * @param {Object} target
 * @param {...Object} others
 * @return {Object} the target
 */
function assign(target) {
  for (
    var _len = arguments.length, others = new Array(_len > 1 ? _len - 1 : 0), _key = 1;
    _key < _len;
    _key++
  ) {
    others[_key - 1] = arguments[_key];
  }

  return _extends.apply(void 0, [target].concat(others));
}

/**
 * Sets a nested property of a given object to the specified value.
 * This mutates the object and returns it.
 * @param {Object} target The target of the set operation.
 * @param {(string|number)[]} path The path to the nested value.
 * @param {any} value The value to set.
 */
function set(target, path, value) {
  var currentTarget = target;
  forEach(path, function (key, idx) {
    if (typeof key !== "number" && typeof key !== "string") {
      throw new Error(
        i18n.default.t("bpmnLint.AlertSet1") + _typeof(key) + i18n.default.t("bpmnLint.AlertSet2")
      );
    }

    if (key === "constructor") {
      throw new Error(i18n.default.t("bpmnLint.AlertSet3"));
    }

    if (key === "__proto__") {
      throw new Error(i18n.default.t("bpmnLint.AlertSet4"));
    }

    var nextKey = path[idx + 1];
    var nextTarget = currentTarget[key];

    if (isDefined(nextKey) && isNil(nextTarget)) {
      nextTarget = currentTarget[key] = isNaN(+nextKey) ? {} : [];
    }

    if (isUndefined(nextKey)) {
      if (isUndefined(value)) {
        delete currentTarget[key];
      } else {
        currentTarget[key] = value;
      }
    } else {
      currentTarget = nextTarget;
    }
  });
  return target;
}

/**
 * Gets a nested property of a given object.
 * @param {Object} target The target of the get operation.
 * @param {(string|number)[]} path The path to the nested value.
 * @param {any} [defaultValue] The value to return if no value exists.
 */
function get(target, path, defaultValue) {
  var currentTarget = target;
  forEach(path, function (key) {
    // accessing nil property yields <undefined>
    if (isNil(currentTarget)) {
      currentTarget = undefined;
      return false;
    }

    currentTarget = currentTarget[key];
  });
  return isUndefined(currentTarget) ? defaultValue : currentTarget;
}

/**
 * Pick given properties from the target object.
 * @param {Object} target
 * @param {Array} properties
 * @return {Object} target
 */
function pick(target, properties) {
  var result = {};
  var obj = Object(target);
  forEach(properties, function (prop) {
    if (prop in obj) {
      result[prop] = target[prop];
    }
  });
  return result;
}

/**
 * Pick all target properties, excluding the given ones.
 * @param {Object} target
 * @param {Array} properties
 * @return {Object} target
 */
function omit(target, properties) {
  var result = {};
  var obj = Object(target);
  forEach(obj, function (prop, key) {
    if (properties.indexOf(key) === -1) {
      result[key] = prop;
    }
  });
  return result;
}

/**
 * Recursively merge `...sources` into given target.
 * Does support merging objects; does not support merging arrays.
 * @param {Object} target
 * @param {...Object} sources
 * @return {Object} the target
 */
function merge(target) {
  for (
    var _len2 = arguments.length, sources = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1;
    _key2 < _len2;
    _key2++
  ) {
    sources[_key2 - 1] = arguments[_key2];
  }

  if (!sources.length) {
    return target;
  }

  forEach(sources, function (source) {
    // skip non-obj sources, i.e. null
    if (!source || !isObject(source)) {
      return;
    }

    forEach(source, function (sourceVal, key) {
      if (key === "__proto__") {
        return;
      }

      var targetVal = target[key];

      if (isObject(sourceVal)) {
        if (!isObject(targetVal)) {
          // override target[key] with object
          targetVal = {};
        }

        target[key] = merge(targetVal, sourceVal);
      } else {
        target[key] = sourceVal;
      }
    });
  });
  return target;
}

/**
 * This module exports an object containing utility functions for various operations.
 * The functions include methods for array manipulation, object property checks,
 * function binding, debouncing, and more. The object is frozen to prevent modification.
 * The module also imports and utilizes an augmented namespace for additional functionality.
 */
var index_esm = /*#__PURE__*/ Object.freeze({
  __proto__: null,
  assign: assign,
  bind: bind,
  debounce: debounce,
  ensureArray: ensureArray,
  every: every,
  filter: filter,
  find: find,
  findIndex: findIndex,
  flatten: flatten$1,
  forEach: forEach,
  get: get,
  groupBy: groupBy,
  has: has,
  isArray: isArray,
  isDefined: isDefined,
  isFunction: isFunction,
  isNil: isNil,
  isNumber: isNumber,
  isObject: isObject,
  isString: isString,
  isUndefined: isUndefined,
  keys: keys,
  map: map,
  matchPattern: matchPattern,
  merge: merge,
  omit: omit,
  pick: pick,
  reduce: reduce,
  set: set,
  size: size,
  some: some,
  sortBy: sortBy,
  throttle: throttle,
  unionBy: unionBy,
  uniqueBy: uniqueBy,
  values: values,
  without: without,
});

var require$$1 = /*@__PURE__*/ getAugmentedNamespace(index_esm);

const { is: is$9 } = require$$0;

const { flatten } = require$$1;

/**
 * Checks for BPMN elements that lack a corresponding BPMNDI reference.
 * This function examines a BPMN Definitions node to identify elements
 * that have a visual representation but do not have a corresponding
 * BPMNDI reference. It reports these elements using the provided reporter.
 * @param {Object} node - The BPMN Definitions node to be checked.
 * @param {Object} reporter - The reporter used to log elements without BPMNDI.
 * @returns {Object} An object containing the check function.
 */
var noBpmndi = function () {
  function check(node, reporter) {
    if (!is$9(node, "bpmn:Definitions")) {
      return false;
    }

    // (1) Construct array of all BPMN elements
    const bpmnElements = getAllBpmnElements(node.rootElements);

    // (2) Filter BPMN elements without visual representation
    const visualBpmnElements = bpmnElements.filter(hasVisualRepresentation);

    // (3) Construct array of BPMNDI references
    const diBpmnReferences = getAllDiBpmnReferences(node);

    // (4) Report elements without BPMNDI
    visualBpmnElements.forEach((element) => {
      if (diBpmnReferences.indexOf(element.id) === -1) {
        reporter.report(element.id, i18n.default.t("bpmnLint.AlertNoBpmndi"));
      }
    });
  }

  return {
    check,
  };
};

/**
 * Get all BPMN elements within a bpmn:Definitions node
 * @param {array<ModdleElement>} rootElements - An array of Moddle rootElements
 * @return {array<Object>} A flat array with all BPMN elements, each represented with { id: elementId, $type: elementType }
 *
 */
function getAllBpmnElements(rootElements) {
  return flatten(
    rootElements.map((rootElement) => {
      // eslint-disable-next-line
      const laneSet =
        // eslint-disable-next-line
        (rootElement.laneSets && rootElement.laneSets[0]) || rootElement.childLaneSet;

      const elements = flatten(
        [].concat(
          rootElement.flowElements || [],
          (rootElement.flowElements &&
            getAllBpmnElements(rootElement.flowElements.filter(hasFlowElements))) ||
            [],
          rootElement.participants || [],
          // eslint-disable-next-line
          rootElement.artifacts || [],
          // eslint-disable-next-line
          (laneSet && laneSet.lanes) || [],
          // eslint-disable-next-line
          (laneSet && laneSet.lanes && getAllBpmnElements(laneSet.lanes.filter(hasChildLaneSet))) ||
            []
        )
      );

      if (elements.length > 0) {
        return elements.map((element) => {
          return {
            id: element.id,
            $type: element.$type,
          };
        });
      } else {
        // We are not interested in the rest here (DI)
        return [];
      }
    })
  );
}

/**
 * Get all BPMN elements within a bpmn:Definitions node
 * @param {ModdleElement} definitionsNode - A moddleElement representing the
 * bpmn:Definitions element
 * @return {array<String>} A flat array with all BPMNDI element ids part of
 * this bpmn:Definitions node
 *
 */
function getAllDiBpmnReferences(definitionsNode) {
  return flatten(
    definitionsNode.diagrams.map((diagram) => {
      const diElements = diagram.plane.planeElement || [];

      return diElements.map((element) => {
        return element.bpmnElement.id;
      });
    })
  );
}

/**
 * Determines if a BPMN element has a visual representation.
 * @param {Object} element - The BPMN element to check.
 * @returns {boolean} - Returns true if the element has a visual representation, false otherwise.
 */
function hasVisualRepresentation(element) {
  const noVisRepresentation = ["bpmn:DataObject"];

  return noVisRepresentation.includes(element.$type) ? false : true;
}

/**
 * Checks if the given element contains flow elements.
 * @param {Object} element - The element to check for flow elements.
 * @returns {boolean} True if the element has flow elements, otherwise false.
 */
function hasFlowElements(element) {
  return element.flowElements ? true : false;
}

/**
 * Determines if the given element has a child lane set.
 * @param {Object} element - The element to check for a child lane set.
 * @returns {boolean} - Returns true if the element has a child lane set, otherwise false.
 */
function hasChildLaneSet(element) {
  return element.childLaneSet ? true : false;
}

var helper = {};

const { is: is$8 } = require$$0;

/**
 * Create a checker that disallows the given element type.
 * @param {String} type
 * @return {Function} ruleImpl
 */
function disallowNodeType$2(type) {
  return function () {
    function check(node, reporter) {
      if (is$8(node, type)) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertDisallowNodeType$2") + type + ">");
      }
    }

    return {
      check,
    };
  };
}

helper.disallowNodeType = disallowNodeType$2;

const disallowNodeType$1 = helper.disallowNodeType;

var noComplexGateway = disallowNodeType$1("bpmn:ComplexGateway");

const { isAny: isAny$2, is: is$7 } = require$$0;

/**
 * Checks if a BPMN node is disconnected, meaning it has no incoming or outgoing connections.
 * The function ignores nodes that are triggered by events or linked to compensation activities.
 * If a node is found to be disconnected, it reports the issue using the provided reporter.
 * @param {Object} node - The BPMN node to check for disconnection.
 * @param {Object} reporter - The reporter object used to log issues.
 */
var noDisconnected = function () {
  function check(node, reporter) {
    if (
      !isAny$2(node, ["bpmn:Task", "bpmn:Gateway", "bpmn:SubProcess", "bpmn:Event"]) ||
      node.triggeredByEvent
    ) {
      return;
    }

    if (isCompensationLinked(node)) {
      return;
    }

    const incoming = node.incoming || [];
    const outgoing = node.outgoing || [];

    if (!incoming.length && !outgoing.length) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoDisconnected"));
    }
  }

  return {
    check,
  };
};

/**
 * Determines if a given node is a compensation boundary event.
 * This function checks if the provided node is a BPMN Boundary Event
 * and if it contains exactly one event definition of type
 * 'bpmn:CompensateEventDefinition'.
 * @param {Object} node - The node to be checked.
 * @returns {boolean} - Returns true if the node is a compensation boundary event, otherwise false.
 */
function isCompensationBoundary(node) {
  var eventDefinitions = node.eventDefinitions;

  if (!is$7(node, "bpmn:BoundaryEvent")) {
    return false;
  }

  if (!eventDefinitions || eventDefinitions.length !== 1) {
    return false;
  }

  return is$7(eventDefinitions[0], "bpmn:CompensateEventDefinition");
}

/**
 * Determines if a given node represents a compensation activity.
 * @param {Object} node - The node to be checked.
 * @returns {boolean} True if the node is marked for compensation, otherwise false.
 */
function isCompensationActivity(node) {
  return node.isForCompensation;
}

/**
 * Determines if a given node is linked to compensation.
 * This function checks if the node is either a compensation boundary event
 * or a compensation activity. It utilizes helper functions to evaluate
 * the node's characteristics and returns true if the node is linked to
 * compensation in any way.
 * @param {Object} node - The node to be evaluated.
 * @returns {boolean} - Returns true if the node is linked to compensation, otherwise false.
 */
function isCompensationLinked(node) {
  var source = isCompensationBoundary(node);
  var target = isCompensationActivity(node);

  return source || target;
}

const { is: is$6 } = require$$0;

/**
 * This function checks for duplicate sequence flows in a BPMN model.
 * It maintains a record of sequence flows using a unique key generated
 * from the flow's source, target, and condition. If a duplicate is found,
 * it reports the duplicate flow and its source and target nodes using the
 * provided reporter. The function ensures that each source and target node
 * is reported only once for duplicates.
 * @returns {Object} An object containing the check function to validate nodes.
 */
var noDuplicateSequenceFlows = function () {
  const keyed = {};

  const outgoingReported = {};
  const incomingReported = {};

  function check(node, reporter) {
    if (!is$6(node, "bpmn:SequenceFlow")) {
      return;
    }

    const key = flowKey(node);

    if (key in keyed) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoDuplicateSequenceFlows1"));

      const sourceId = node.sourceRef.id;
      const targetId = node.targetRef.id;

      if (!outgoingReported[sourceId]) {
        reporter.report(sourceId, i18n.default.t("bpmnLint.AlertNoDuplicateSequenceFlows2"));

        outgoingReported[sourceId] = true;
      }

      if (!incomingReported[targetId]) {
        reporter.report(targetId, i18n.default.t("bpmnLint.AlertNoDuplicateSequenceFlows3"));

        incomingReported[targetId] = true;
      }
    } else {
      keyed[key] = node;
    }
  }

  return {
    check,
  };
};

/**
 * Generates a unique key for a given flow object by concatenating
 * the source reference ID, target reference ID, and condition expression.
 * @param {Object} flow - The flow object containing details of the flow.
 * @param {Object} [flow.conditionExpression] - The condition expression of the flow.
 * @param {Object} [flow.sourceRef] - The source reference of the flow.
 * @param {Object} [flow.targetRef] - The target reference of the flow.
 * @returns {string} A string key in the format "source#target#condition".
 */
function flowKey(flow) {
  const conditionExpression = flow.conditionExpression;

  const condition = conditionExpression ? conditionExpression.body : "";
  const source = flow.sourceRef ? flow.sourceRef.id : flow.id;
  const target = flow.targetRef ? flow.targetRef.id : flow.id;

  return source + "#" + target + "#" + condition;
}

const { is: is$5 } = require$$0;

/**
 * Function to check for improper gateway configurations in a BPMN model.
 * Specifically, it identifies gateways with multiple incoming and outgoing
 * sequence flows, which is not allowed. If such a gateway is found, it reports
 * the issue using the provided reporter.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log issues.
 * @returns {Object} An object containing the check function.
 */
var noGatewayJoinFork = function () {
  function check(node, reporter) {
    if (!is$5(node, "bpmn:Gateway")) {
      return;
    }

    const incoming = node.incoming || [];
    const outgoing = node.outgoing || [];

    if (incoming.length > 1 && outgoing.length > 1) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoGatewayJoinFork"));
    }
  }

  return {
    check,
  };
};

const { isAny: isAny$1 } = require$$0;

/**
 * Checks for implicit splits in BPMN diagrams by verifying if a node of type
 * "bpmn:Task" or "bpmn:Event" has more than one outgoing flow without a condition
 * or default flow. If such a case is found, it reports an alert using the provided
 * reporter.
 * @returns {Object} An object containing the check function.
 */
var noImplicitSplit = function () {
  function check(node, reporter) {
    if (!isAny$1(node, ["bpmn:Task", "bpmn:Event"])) {
      return;
    }

    const outgoing = node.outgoing || [];

    const outgoingWithoutCondition = outgoing.filter((flow) => {
      return !hasCondition(flow) && !isDefaultFlow(node, flow);
    });

    if (outgoingWithoutCondition.length > 1) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoImplicitSplit"));
    }
  }

  return {
    check,
  };
};

/**
 * Checks if a given flow has a condition expression.
 * @param {Object} flow - The flow object to check.
 * @returns {boolean} True if the flow has a condition expression, otherwise false.
 */
function hasCondition(flow) {
  return !!flow.conditionExpression;
}

/**
 * Determines if the specified flow is the default flow for a given node.
 * @param {Object} node - The node object containing flow information.
 * @param {Object} flow - The flow to check against the node's default flow.
 * @returns {boolean} - Returns true if the flow is the default flow of the node, otherwise false.
 */
function isDefaultFlow(node, flow) {
  return node["default"] === flow;
}

const disallowNodeType = helper.disallowNodeType;

var noInclusiveGateway = disallowNodeType("bpmn:InclusiveGateway");

const { is: is$4 } = require$$0;

/**
 * Checks for multiple blank start events within a BPMN flow elements container.
 * A blank start event is defined as a start event without any event definitions.
 * If more than one blank start event is found, it reports an alert using the reporter.
 * @param {Object} node - The BPMN node to be checked, expected to be a FlowElementsContainer.
 * @param {Object} reporter - The reporter object used to report any findings.
 */
var singleBlankStartEvent = function () {
  function check(node, reporter) {
    if (!is$4(node, "bpmn:FlowElementsContainer")) {
      return;
    }

    const flowElements = node.flowElements || [];

    const blankStartEvents = flowElements.filter(function (flowElement) {
      if (!is$4(flowElement, "bpmn:StartEvent")) {
        return false;
      }

      const eventDefinitions = flowElement.eventDefinitions || [];

      return eventDefinitions.length === 0;
    });

    if (blankStartEvents.length > 1) {
      let type = null;
      if (is$4(node, "bpmn:SubProcess")) {
        type = i18n.default.t("bpmnLint.AlertSingleBlankStartEvent");
      } else {
        type = i18n.default.t("bpmnLint.AlertSingleBlankStartEvent");
      }

      reporter.report(node.id, type);
    }
  }

  return {
    check,
  };
};

const { is: is$3 } = require$$0;

/**
 * Checks if a BPMN event node has more than one event definition.
 * If multiple event definitions are found, it reports an issue using the provided reporter.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log issues.
 */
var singleEventDefinition = function () {
  function check(node, reporter) {
    if (!is$3(node, "bpmn:Event")) {
      return;
    }

    const eventDefinitions = node.eventDefinitions || [];

    if (eventDefinitions.length > 1) {
      reporter.report(
        node.id,
        i18n.default.t("bpmnLint.AlertSingleEventDefinition")["eventDefinitions"]
      );
    }
  }

  return {
    check,
  };
};

const { is: is$2, isAny } = require$$0;

/**
 * Checks if a BPMN process or subprocess node contains a start event.
 * If a start event is missing, reports an alert using the provided reporter.
 * Utilizes organization-specific i18n for alert messages.
 * @returns {Object} An object containing the check function.
 */
var startEventRequired = function () {
  function hasStartEvent(node) {
    const flowElements = node.flowElements || [];

    return flowElements.some((node) => is$2(node, "bpmn:StartEvent"));
  }

  function check(node, reporter) {
    if (!isAny(node, ["bpmn:Process", "bpmn:SubProcess"])) {
      return;
    }

    if (!hasStartEvent(node)) {
      let type = null;
      if (is$2(node, "bpmn:SubProcess")) {
        type = i18n.default.t("bpmnLint.AlertStartEventRequired");
      } else {
        type = i18n.default.t("bpmnLint.AlertStartEventRequired");
      }

      reporter.report(node.id, type);
    }
  }

  return { check };
};

const { is: is$1 } = require$$0;

/**
 * Checks if a BPMN SubProcess node contains a blank StartEvent.
 * If a StartEvent within the SubProcess has event definitions, it reports an alert.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log alerts.
 * @returns {Object} An object containing the check function.
 */
var subProcessBlankStartEvent = function () {
  function check(node, reporter) {
    if (!is$1(node, "bpmn:SubProcess") || node.triggeredByEvent) {
      return;
    }

    const flowElements = node.flowElements || [];

    flowElements.forEach(function (flowElement) {
      if (!is$1(flowElement, "bpmn:StartEvent")) {
        return false;
      }

      const eventDefinitions = flowElement.eventDefinitions || [];

      if (eventDefinitions.length > 0) {
        reporter.report(flowElement.id, i18n.default.t("bpmnLint.AlertSubProcessBlankStartEvent"), [
          "eventDefinitions",
        ]);
      }
    });
  }

  return {
    check,
  };
};

const { is } = require$$0;

/**
 * Checks for superfluous gateways in a BPMN model.
 * A gateway is considered superfluous if it has exactly one incoming and one outgoing flow.
 * If such a gateway is found, it reports the issue using the provided reporter.
 * @param {Object} node - The BPMN node to check.
 * @param {Object} reporter - The reporter used to log issues.
 */
var superfluousGateway = function () {
  function check(node, reporter) {
    if (!is(node, "bpmn:Gateway")) {
      return;
    }

    const incoming = node.incoming || [];
    const outgoing = node.outgoing || [];

    if (incoming.length === 1 && outgoing.length === 1) {
      reporter.report(node.id, i18n.default.t("bpmnLint.AlertSuperfluousGateway"));
    }
  }

  return {
    check,
  };
};

const cache = {};

/**
 * Checks if an Exclusive Gateway in a BPMN model returns to a User Task, Manual Task, or End Event.
 * If the gateway's outgoing sequence flows do not lead to any of these elements, it reports an issue.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log issues.
 * @returns {Object} An object containing the check function.
 */
var exclusiveGatewayReturnUserTask = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      const outgoing = node.outgoing || [];

      for (let outgoingPosition = 0; outgoingPosition < outgoing.length; outgoingPosition++) {
        const outgoingNode = outgoing[outgoingPosition];
        const targetRef = outgoingNode.targetRef;

        if (
          !is(targetRef, "bpmn:ManualTask") &&
          !is(targetRef, "bpmn:UserTask") &&
          !is(targetRef, "bpmn:SendTask") &&
          !is(targetRef, "bpmn:EndEvent")
        ) {
          reporter.report(node.id, i18n.default.t("bpmnLint.AlertExclusiveGatewayReturnUserTask"));
          return;
        }
      }
    }
  }

  return {
    check,
  };
};

/**
 * Checks for the presence of an Exclusive Gateway immediately following a Start Event
 * in a BPMN diagram. If such a pattern is detected, it reports an alert using the
 * provided reporter function.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log alerts.
 * @returns {Object} An object containing the check function.
 */
var noExclusiveGatewayAfterStartEvent = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      const incoming = node.incoming || [];

      if (incoming.length === 1 && incoming[0].sourceRef.$type === "bpmn:StartEvent") {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoExclusiveGatewayAfterStartEvent"));
      }
    }
  }

  return {
    check,
  };
};

/**
 * Checks if an exclusive gateway node has two outgoing sequence flows
 * without names and reports it if found.
 * @param {Object} node - The BPMN node to check.
 * @param {Object} reporter - The reporter to log issues.
 * @returns {Object} An object containing the check function.
 */
var exclusiveGatewayOutgoingWihtoutName = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      const outgoing = node.outgoing || [];

      if (
        outgoing.length === 2 &&
        outgoing[0].$type === "bpmn:SequenceFlow" &&
        outgoing[1].$type === "bpmn:SequenceFlow"
      ) {
        const hasUnnamedOutgoing = !outgoing[0].name || !outgoing[1].name;

        if (hasUnnamedOutgoing) {
          reporter.report(
            node.id,
            i18n.default.t("bpmnLint.AlertExclusiveGatewayOutgoingWihtoutName")
          );
        }
      }
    }
  }

  return {
    check,

    events: {
      "element.changed": function (event, reporter) {
        const { element } = event;

        if (is(element, "bpmn:ExclusiveGateway")) {
          check(element, reporter);
        }
      },
    },
  };
};

/**
 * Function to track and report excessive usage of Exclusive Gateways in BPMN diagrams.
 *  A rule that checks, restrict saving the workflow model if 10 gates are used in the same flow,
 * @returns {Object} An object containing a function to check for excessive Exclusive Gateways.
 */
var avoidMoreThanTenExclusiveGateways = function () {
  var exclusiveGatewayCount = 0;

  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      exclusiveGatewayCount++;
      if (exclusiveGatewayCount > 20) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertAvoidMoreThanTenExclusiveGateways"));
      }
    }
  }

  return {
    check,
  };
};

/**
 * Checks if a BPMN Exclusive Gateway has exactly two outgoing flows
 * leading to End Events. If so, reports an issue using the provided reporter.
 * @function
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log issues.
 * @returns {Object} An object containing the check function.
 */
var exclusiveGatewayNoToEndEvent = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      const outgoing = node.outgoing || [];

      if (outgoing.length === 2) {
        let endEventCount = 0;

        outgoing.forEach(function (outgoingNode) {
          if (is(outgoingNode.targetRef, "bpmn:EndEvent")) {
            endEventCount++;
          }
        });

        if (endEventCount === 2) {
          reporter.report(node.id, i18n.default.t("bpmnLint.AlertExclusiveGatewayNoToEndEvent"));
        }
      }
    }
  }

  return {
    check,
  };
};

/**
 * Validates that a BPMN Exclusive Gateway node has exactly two outgoing connections.
 * @function
 * @returns {Object} An object containing the check function.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log validation errors.
 */
var exclusiveGatewayOnlyTwoOutgoing = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:ExclusiveGateway")) {
      const outgoing = node.outgoing || [];

      if (outgoing.length !== 2) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertExclusiveGatewayOnlyTwoOutgoing"));
      }
    }
  }

  return {
    check: check,
  };
};

/**
 * Checks if a BPMN SendTask node is improperly connected to an ExclusiveGateway
 * or another SendTask. If such a connection is found, it reports an error.
 * @function
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log errors.
 */
var sendTaskNoConnectExclusiveGatewayOrSendTask = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:SendTask")) {
      const outgoing = node.outgoing || [];

      for (let outgoingPosition = 0; outgoingPosition < outgoing.length; outgoingPosition++) {
        const outgoingNode = outgoing[outgoingPosition];
        const targetRef = outgoingNode.targetRef;

        if (is(targetRef, "bpmn:ExclusiveGateway")) {
          reporter.report(node.id, i18n.default.t("bpmnLint.AlertSendTaskNoConectionAtgateway"));
          return;
        }

        if (is(targetRef, "bpmn:SendTask")) {
          reporter.report(node.id, i18n.default.t("bpmnLint.AlertSendTaskNoConectionAtSendTask"));
          return;
        }
      }
    }
  }

  return {
    check,
  };
};

/**
 * Rule to ensure that no more than five 'bpmn:SendTask' elements are present in a BPMN model.
 * This function maintains a count of 'bpmn:SendTask' elements and reports a violation
 * if the count exceeds five. It uses a reporter to log the issue with the specific node ID.
 * @returns {Object} An object containing the 'check' function to validate nodes.
 */
var avoidMoreThanFiveSendTasks = function () {
  var sendTaskCount = 0;

  function check(node, reporter) {
    if (is(node, "bpmn:SendTask")) {
      sendTaskCount++;
      if (sendTaskCount > 5) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertSendTaskMoreThanFiveSendTask"));
      }
    }
  }

  return {
    check,
  };
};

/**
 * Rule implementation to avoid multiple end events in a BPMN diagram.
 * This function checks each node in the diagram to determine if it is an
 * 'EndEvent'. If more than one 'EndEvent' is found, it reports an alert
 * using the provided reporter.
 * @returns {Object} An object containing the 'check' function to validate nodes.
 */
var avoidTwoEndEvents = function () {
  var hasEndEvent = false;

  function check(node, reporter) {
    if (is(node, "bpmn:EndEvent")) {
      if (hasEndEvent) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertAvoidTwoEndEvents"));
      } else {
        hasEndEvent = true;
      }
    }
  }

  return {
    check,
  };
};

/**
 * Function to check for terminate end events in a BPMN model.
 * This function inspects nodes to identify if they are end events
 * with terminate event definitions. If such nodes are found, it
 * reports them using the provided reporter.
 * @param {Object} e - The context or environment in which the function operates.
 * @returns {Object} An object containing the check function.
 */
var avoidTerminateEndEventsAsEnd = function () {
  function check(node, reporter) {
    if (is(node, "bpmn:EndEvent")) {
      const eventDefinitions = node.eventDefinitions || [];

      eventDefinitions.forEach(function (eventDef) {
        if (eventDef.$type === "bpmn:TerminateEventDefinition") {
          reporter.report(node.id, i18n.default.t("bpmnLint.AlertAvoidTerminateEndEventsAsEnd"));
        }
      });
    }
  }

  return {
    check,
  };
};

/**
 * Checks if a BPMN node is unconnected, specifically for UserTask, ManualTask,
 * SendTask, and Gateway types, excluding StartEvent and EndEvent nodes.
 * Reports an alert if the node has no incoming or outgoing connections.
 * @function
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log alerts.
 * @returns {Object} An object containing the check function.
 */
var noUnconnectedFlowNode = function () {
  function check(node, reporter) {
    if (
      isAny(node, ["bpmn:UserTask", "bpmn:ManualTask", "bpmn:SendTask", "bpmn:Gateway"]) &&
      !isAny(node, ["bpmn:StartEvent", "bpmn:EndEvent"])
    ) {
      const outgoing = node.outgoing || [];
      const incoming = node.incoming || [];

      if (outgoing.length === 0 || incoming.length === 0) {
        reporter.report(node.id, i18n.default.t("bpmnLint.AlertNoDisconnected"));
      }
    }
  }

  return {
    check: check,
  };
};

/**
 * Checks if specific BPMN nodes (UserTask, ManualTask, SendTask, Gateway)
 * have backward connections in the flow. If a node connects to a previous
 * node in the flow, it reports an error using the provided reporter.
 * @param {Object} node - The BPMN node to be checked.
 * @param {Object} reporter - The reporter object used to log errors.
 * @returns {Object} An object containing the check function.
 */
var noBackwardConnectionsForSelectedTasks = function () {
  function check(node, reporter) {
    if (
      is(node, "bpmn:UserTask") ||
      is(node, "bpmn:ManualTask") ||
      is(node, "bpmn:SendTask") ||
      is(node, "bpmn:Gateway")
    ) {
      const outgoing = node.outgoing || [];
      const id = node.id;
      const visited = [id];
      let current = node;
      let prev = null;

      while (!is(current, "bpmn:StartEvent")) {
        const incoming = current.incoming || [];

        if (incoming.length > 0) {
          prev = incoming[0].sourceRef;
        }

        if (prev && visited.includes(prev.id)) {
          reporter.report(
            current.id,
            i18n.default.t("bpmnLint.AlertExclusiveGatewayPreviousElements")
          );
          break;
        }

        if (prev) {
          visited.push(prev.id);
          current = prev;
        } else {
          break;
        }
      }

      outgoing.forEach((flow) => {
        if (!flow.targetRef || visited.includes(flow.targetRef.id)) {
          reporter.report(
            flow.id,
            i18n.default.t("bpmnLint.AlertExclusiveGatewayPreviousElements")
          );
        }
      });
    }
  }

  return {
    check,
  };
};

/**
 * Resolves a rule from the cache based on the provided package and rule name.
 * @param {string} pkg - The package name to resolve the rule from.
 * @param {string} ruleName - The name of the rule to be resolved.
 * @returns {*} The resolved rule from the cache.
 * @throws {Error} If the rule is not found in the cache, an error is thrown with a localized message.
 */
function Resolver() {}
Resolver.prototype.resolveRule = function (pkg, ruleName) {
  const rule = cache[pkg + "/" + ruleName];

  if (!rule) {
    throw new Error(i18n.default.t("bpmnLint.AlertResolveRule1") + pkg + "/" + ruleName + ">");
  }

  return rule;
};

/**
 * Resolves the configuration for a given package and configuration name.
 * This method throws an error indicating that the configuration resolution
 * is not implemented or fails.
 * @param {string} pkg - The name of the package for which to resolve the configuration.
 * @param {string} configName - The name of the configuration to resolve.
 * @throws {Error} Throws an error with a message indicating the failure to resolve the configuration.
 */
Resolver.prototype.resolveConfig = function (pkg, configName) {
  throw new Error(
    i18n.default.t("bpmnLint.AlertResolveRule1") +
      configName +
      i18n.default.t("bpmnLint.AlertResolveRule2") +
      pkg +
      ">"
  );
};

const resolver = new Resolver();

const rules = {
  "conditional-flows": "error",
  "end-event-required": "error",
  "event-sub-process-typed-start-event": "error",
  "task-event-required": "error",
  "label-required": "error",
  "no-bpmndi": "error",
  "no-complex-gateway": "error",
  "no-disconnected": "error",
  "no-duplicate-sequence-flows": "error",
  "no-gateway-join-fork": "error",
  "no-implicit-split": "error",
  "no-inclusive-gateway": "error",
  "single-blank-start-event": "error",
  "single-event-definition": "error",
  "start-event-required": "error",
  "sub-process-blank-start-event": "error",
  "superfluous-gateway": "error",
  "no-exclusive-gateway-after-start-event": "error",
  "exclusive-gateway-return-user-task": "error",
  "exclusive-gateway-no-to-end-event": "error",
  "exclusive-gateway-outgoing-wihtout-name": "error",
  "avoid-More-Than-Ten-ExclusiveGateways": "error",
  "exclusive-gateway-only-two-outgoing": "error",
  "sendTask-No-Connect-ExclusiveGateway-Or-SendTask": "error",
  "avoid-More-Than-Five-SendTasks": "error",
  "avoid-two-end-events": "error",
  "avoid-terminate-end-events-as-end": "error",
  "no-unconnected-flow-node": "error",
  "no-Backward-Connections-For-SelectedTasks": "error",
};

const config = {
  rules: rules,
};

const bundle = {
  resolver: resolver,
  config: config,
};

cache["bpmnlint/conditional-flows"] = conditionalFlows;
cache["bpmnlint/end-event-required"] = endEventRequired;
cache["bpmnlint/task-event-required"] = TaskRequired;
cache["bpmnlint/event-sub-process-typed-start-event"] = eventSubProcessTypedStartEvent;
cache["bpmnlint/label-required"] = labelRequired;
cache["bpmnlint/no-bpmndi"] = noBpmndi;
cache["bpmnlint/no-complex-gateway"] = noComplexGateway;
cache["bpmnlint/no-disconnected"] = noDisconnected;
cache["bpmnlint/no-duplicate-sequence-flows"] = noDuplicateSequenceFlows;
cache["bpmnlint/no-gateway-join-fork"] = noGatewayJoinFork;
cache["bpmnlint/no-implicit-split"] = noImplicitSplit;
cache["bpmnlint/no-inclusive-gateway"] = noInclusiveGateway;
cache["bpmnlint/single-blank-start-event"] = singleBlankStartEvent;
cache["bpmnlint/single-event-definition"] = singleEventDefinition;
cache["bpmnlint/start-event-required"] = startEventRequired;
cache["bpmnlint/sub-process-blank-start-event"] = subProcessBlankStartEvent;
cache["bpmnlint/superfluous-gateway"] = superfluousGateway;
cache["bpmnlint/no-exclusive-gateway-after-start-event"] = noExclusiveGatewayAfterStartEvent;
cache["bpmnlint/exclusive-gateway-return-user-task"] = exclusiveGatewayReturnUserTask;
cache["bpmnlint/exclusive-gateway-no-to-end-event"] = exclusiveGatewayNoToEndEvent;
cache["bpmnlint/exclusive-gateway-outgoing-wihtout-name"] = exclusiveGatewayOutgoingWihtoutName;
cache["bpmnlint/avoid-More-Than-Ten-ExclusiveGateways"] = avoidMoreThanTenExclusiveGateways;
cache["bpmnlint/exclusive-gateway-only-two-outgoing"] = exclusiveGatewayOnlyTwoOutgoing;
cache[
  "bpmnlint/sendTask-No-Connect-ExclusiveGateway-Or-SendTask"
] = sendTaskNoConnectExclusiveGatewayOrSendTask;
cache["bpmnlint/avoid-More-Than-Five-SendTasks"] = avoidMoreThanFiveSendTasks;
cache["bpmnlint/avoid-two-end-events"] = avoidTwoEndEvents;
cache["bpmnlint/avoid-terminate-end-events-as-end"] = avoidTerminateEndEventsAsEnd;
cache["bpmnlint/no-unconnected-flow-node"] = noUnconnectedFlowNode;

cache["bpmnlint/no-Backward-Connections-For-SelectedTasks"] = noBackwardConnectionsForSelectedTasks;

exports.config = config;
exports["default"] = bundle;
exports.resolver = resolver;
